import React from 'react';
import { Info } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { AUTO_CALENDAR } from '../bookingLinkForm';
import { FieldLabel, FieldHint } from './fields';

/**
 * Step 3 — where the meeting happens.
 *
 * The hosting calendar (the provider — "Calendar" in the flat editor) and the
 * location are one relationship (#514 D5): the conference option follows the
 * calendar that can mint it. #685 states that rule up front and explains a
 * fallback when it fires; the fallback itself lives in BookingLinkModal.
 */
export default function IntegrationSection({ form, locationOptions, onCalendarChange, onLocationChange, locationNotice }) {
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <FieldLabel htmlFor="bl-calendar">Hosting calendar</FieldLabel>
                    <Select value={form.calendar || AUTO_CALENDAR} onValueChange={onCalendarChange}>
                        <SelectTrigger id="bl-calendar" className="mt-1.5" data-testid="bl-calendar"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value={AUTO_CALENDAR}>Auto — match the assignee's connected calendar</SelectItem>
                            <SelectItem value="google">Google Calendar</SelectItem>
                            <SelectItem value="outlook">Outlook Calendar</SelectItem>
                        </SelectContent>
                    </Select>
                    <FieldHint>Google or Outlook — hosts each booking's event and invitation.</FieldHint>
                </div>
                <div>
                    <FieldLabel htmlFor="bl-location">Location</FieldLabel>
                    <Select value={form.location} onValueChange={onLocationChange}>
                        <SelectTrigger id="bl-location" className="mt-1.5" data-testid="bl-location"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {locationOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {locationNotice && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-1.5" role="status" data-testid="bl-location-notice">
                            {locationNotice}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5" data-testid="bl-location-rule">
                <Info className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" aria-hidden="true" />
                <p className="text-xs text-stone-600">
                    Location options follow the hosting calendar: Google Meet needs Google Calendar and Microsoft Teams needs Outlook.
                    Changing the calendar swaps a location it can't create for the first one it can. <strong>Auto</strong> keeps both on offer, because either calendar may end up hosting.
                </p>
            </div>
        </div>
    );
}
